import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./component/scrollToTop/ScrollToTop";
import "bootstrap/dist/css/bootstrap.min.css";

// Home Pages Import
import DigitalAgency from "./pages/DigitalAgency";
import MvpPage from "./pages/MvpPage";

// Blog Import
import Blogs from "./pages/Blogs";
import BlogCategory from "./pages/Category";
import BlogArchive from "./pages/Archive";
import BlogDetails from "./pages/BlogDetails";

// Service
import ServiceOne from "./pages/ServiceOne";
import ServiceDetails from "./pages/ServiceDetails";

// Project
import ProjectGridThree from "./pages/ProjectGridThree";

// Pages
import AboutUs from "./pages/AboutUs";
import VisiterManageSystem from "./pages/VisiterManageSystem";
import Contact from "./pages/Contact";
import Contactdigi from "./pages/Contactdigi";
import ErrorPage from "./pages/404";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";

// Css Import
import "./assets/scss/app.scss";
import Consultancy from "./pages/Consultancy";
import StaffAugmentation from "./pages/Staff-Augmentation";
import Career from "./pages/Career";
import FaqOne from "./component/faq/FaqOne";
import WebDevelopment from "./pages/WebDevelopment";
import MobileAppDevelopment from "./pages/MobileAppDevelopment";
import Design from "./pages/Design";
import { Helmet } from "react-helmet-async";
import PortfolioProjectDetails from "./pages/PortfolioProjectDetails";

import SocialMediaMarketing from "./pages/SocialMediaMarketing";

import PerformanceMarketing from "./pages/PerformanceMarketing";
import SearchEngineOptimization from "./pages/SearchEngineOptimization";
import GraphicDesigning from "./pages/GraphicDesigning";
import CookieConsentBanner from "./component/consent/CookieConsentBanner";

// Organization/WebSite nodes describe the site as a whole and are valid on
// every route. The WebPage/BreadcrumbList nodes describe a specific page, so
// they are only accurate — and only rendered — on the homepage itself, to
// avoid every route claiming to be the homepage.
const SITE_URL = "https://itcodeinfotech.com";

const websiteNode = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "url": SITE_URL,
  "name": "ITCODE InfoTech",
  "description": "An IT services and consulting firm providing clients with digital solutions to grow their business reach on a global scale.",
  "publisher": {
    "@id": `${SITE_URL}/#organization`
  },
  "inLanguage": "en-US"
};

const organizationNode = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  "name": "ITCODE InfoTech",
  "url": SITE_URL,
  "logo": {
    "@type": "ImageObject",
    "inLanguage": "en-US",
    "@id": `${SITE_URL}/#/schema/logo/image/`,
    "url": `${SITE_URL}/static/media/logo.4289e6f7.svg`,
    "contentUrl": `${SITE_URL}/static/media/logo.4289e6f7.svg`,
    "width": 173,
    "height": 50,
    "caption": "ITCODE InfoTech"
  },
  "image": {
    "@id": `${SITE_URL}/static/media/logo.4289e6f7.svg`
  },
  "sameAs": [
    "https://www.linkedin.com/company/itcodehelp/",
    "https://www.instagram.com/itcode_digital/"
  ]
};

const homeWebPageNode = {
  "@type": "WebPage",
  "@id": `${SITE_URL}/`,
  "url": SITE_URL,
  "name": "ITCODE InfoTech | Best Web Development Company",
  "isPartOf": {
    "@id": `${SITE_URL}/#website`
  },
  "about": {
    "@id": `${SITE_URL}/#organization`
  },
  "datePublished": "2022-09-20T11:49:53+00:00",
  "dateModified": "2026-08-25T00:00:00+00:00",
  "description": "An IT services and consulting firm providing clients with digital solutions to grow their business reach on a global scale.",
  "breadcrumb": {
    "@id": `${SITE_URL}/#breadcrumb`
  },
  "inLanguage": "en-US",
  "potentialAction": [
    {
      "@type": "ReadAction",
      "target": [SITE_URL]
    }
  ]
};

const homeBreadcrumbNode = {
  "@type": "BreadcrumbList",
  "@id": `${SITE_URL}/#breadcrumb`,
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home"
    }
  ]
};

// Must render inside <Router> to read the current route via useLocation.
const GlobalSchema = () => {
  const location = useLocation();
  const isHome = location.pathname === process.env.PUBLIC_URL + "/";
  const graph = isHome
    ? [homeWebPageNode, homeBreadcrumbNode, websiteNode, organizationNode]
    : [websiteNode, organizationNode];

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": graph
        })}
      </script>
    </Helmet>
  );
};

const App = () => {
  return (
    <>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <GlobalSchema />
        <ScrollToTop>
          <Routes>
            <Route
              path={process.env.PUBLIC_URL + "/"}
              element={<DigitalAgency />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/mvp"}
              element={<MvpPage />}
            />

            {/* Blogs — /blogs/page/N is a real path so each page is a distinct,
                separately prerendered URL (not just a query string collapsing
                to the same static file). */}
            <Route
              path={process.env.PUBLIC_URL + "/blogs"}
              element={<Blogs />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/blogs/page/:pageNum"}
              element={<Blogs />}
            />
            {/* Old template route, replaced by /blogs — redirect to preserve any existing links */}
            <Route
              path={process.env.PUBLIC_URL + "/blog-grid"}
              element={<Navigate to={process.env.PUBLIC_URL + "/blogs"} replace />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/category/:slug"}
              element={<BlogCategory />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/archive/:slug"}
              element={<BlogArchive />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/blog-details/:slug"}
              element={<BlogDetails />}
            />

            {/* Service */}
            <Route
              path={process.env.PUBLIC_URL + "/service/"}
              element={<ServiceOne />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/service-details/:slug"}
              element={<ServiceDetails />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/social-media-marketing"}
              element={<SocialMediaMarketing />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/performance-marketing-services"}
              element={<PerformanceMarketing />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/search-engine-optimization"}
              element={<SearchEngineOptimization />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/graphic-designing-and-branding-services"}
              element={<GraphicDesigning />}
            />
            {/* Project  */}
            {/* Old template route, replaced by /portfolio — redirect to preserve any existing links */}
            <Route
              path={process.env.PUBLIC_URL + "/project-grid-two"}
              element={<Navigate to={process.env.PUBLIC_URL + "/portfolio"} replace />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/portfolio"}
              element={<ProjectGridThree />}
            />
            {/* Folded into /portfolio — redirect to preserve any existing links/bookmarks */}
            <Route
              path={process.env.PUBLIC_URL + "/digital-marketing-portfolio"}
              element={<Navigate to={process.env.PUBLIC_URL + "/portfolio"} replace />}
            />
            {/* Old template route, superseded by /Portfolio-project-details — redirect to preserve any existing links */}
            <Route
              path={process.env.PUBLIC_URL + "/project-details/:slug"}
              element={<Navigate to={process.env.PUBLIC_URL + "/portfolio"} replace />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/Portfolio-project-details/:slug"}
              element={<PortfolioProjectDetails />}
            />
            {/* Pages  */}
            <Route
              path={process.env.PUBLIC_URL + "/about-us"}
              element={<AboutUs />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/visitor-management-system"}
              element={<VisiterManageSystem />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/career"}
              element={<Career />}
            />
            {/* Old template route: unlinked from the live site, rendered leftover
                placeholder bio content — redirect to the About page instead. */}
            <Route
              path={process.env.PUBLIC_URL + "/team-details/:slug"}
              element={<Navigate to={process.env.PUBLIC_URL + "/about-us"} replace />}
            />
            {/* Old template route, never linked from the live site — redirect to homepage */}
            <Route
              path={process.env.PUBLIC_URL + "/testimonials"}
              element={<Navigate to={process.env.PUBLIC_URL + "/"} replace />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/contact"}
              element={<Contact />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/contact-digi"}
              element={<Contactdigi />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/consultancy"}
              element={<Consultancy />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/Staff-Augmentation"}
              element={<StaffAugmentation />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/web-development"}
              element={<WebDevelopment />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/mobile-app-development"}
              element={<MobileAppDevelopment />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/ui-ux-design"}
              element={<Design />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/404"}
              element={<ErrorPage />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/privacy-policy"}
              element={<PrivacyPolicy />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/terms-use"}
              element={<TermsOfUse />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/faqs"}
              element={<FaqOne />}
            />
            {/* Catch-all: any unmatched URL gets a real 404 page with header/footer, not a blank shell */}
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </ScrollToTop>
      </Router>
      <CookieConsentBanner />
    </>
  );
};

export default App;
